import { z } from "zod";

/** An explicit qualification, never inferred from legacy free text. */
export const CENSUS_COVERAGE_CHOICES = [
  "individual",
  "couple",
  "single_parent",
  "family",
  "gfs_exempt_spouse",
] as const;
export const CensusCoverageChoiceSchema = z.enum(CENSUS_COVERAGE_CHOICES);
export type CensusCoverageChoice = z.infer<typeof CensusCoverageChoiceSchema>;

export const CENSUS_COVERAGE_LABELS: Record<
  CensusCoverageChoice,
  { readonly fr: string; readonly en: string }
> = {
  individual: { fr: "Individuel", en: "Individual" },
  couple: { fr: "Couple", en: "Couple" },
  single_parent: { fr: "Monoparental", en: "Single parent" },
  family: { fr: "Familial", en: "Family" },
  gfs_exempt_spouse: {
    fr: "Exempté du régime GFS entier — assuré avec son conjoint",
    en: "Exempt from the entire GFS plan — insured through spouse",
  },
};

export type CensusCoverage = {
  coverage?: string | null;
  coverage_choice?: CensusCoverageChoice | null;
};

/** The old projection remains byte-identical when no choice was made. */
export function censusCoverageLabel(row: CensusCoverage, language: "fr" | "en" = "fr"): string {
  return row.coverage_choice
    ? CENSUS_COVERAGE_LABELS[row.coverage_choice][language]
    : (row.coverage ?? "");
}

/**
 * A legacy PUT must not erase a choice or hide a raw-text correction behind it.
 * The caller holds the census lock before reading the previous row.
 */
export function resolveCensusCoverageWrite(
  previous: CensusCoverage | undefined,
  incoming: CensusCoverage,
): { conflict: true } | { conflict: false; choice: CensusCoverageChoice | null } {
  if (incoming.coverage_choice !== undefined)
    return { conflict: false, choice: incoming.coverage_choice };
  if (previous?.coverage_choice && (incoming.coverage ?? null) !== (previous.coverage ?? null))
    return { conflict: true };
  return { conflict: false, choice: previous?.coverage_choice ?? null };
}
